import { FC, useContext } from "react";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { DocViewContext, PageTabVariant } from "providers/DocViewProvider";
import { useAppSelector } from "store";
import {
  pageSelectors,
  selectCurrentSectionIsTemplate,
  selectFullSubmissionById,
  selectIsEditModeEnabled,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { usePresentation } from "utils/presentation";
import { useQueryParam } from "utils/routing";

interface PageReleasedBannerProps {
  pageId: string;
  selectedTabVariant?: PageTabVariant;
}

export const PageReleasedBanner: FC<PageReleasedBannerProps> = ({ pageId, selectedTabVariant }) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["released_at"]);
  const { canAuthorPage } = useContext(DocViewContext);
  const { showWarning, bypassWarning } = useContext(BypassReleaseWarningContext);
  const { isPresenting } = usePresentation();

  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const isSubmissionsTab =
    selectedTabVariant === "my_submission" ||
    selectedTabVariant === "student_submission" ||
    !!submission;

  const isTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);

  const showPageReleasedBanner =
    isEditModeEnabled &&
    canAuthorPage &&
    page?.released_at &&
    !isPresenting &&
    showWarning &&
    !isTemplateSection &&
    !isSubmissionsTab;

  if (!showPageReleasedBanner) {
    return null;
  }

  return (
    <Banner kind="warning" onDismiss={bypassWarning}>
      {t("warning.page_released")}
    </Banner>
  );
};
